import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CancelReasonCode, DeclineReasonCode, OrderStatus, SellerOrderDetail } from "@/lib/api/types";
import { useOrderTransition } from "@/modules/orders/services/useOrderTransition";
import {
  getAllValidTargets,
  getAllowedTransitions,
} from "@/modules/orders/lib/orderStatusMachine";
import { getStatusMeta } from "@/modules/orders/lib/order-status-meta";
import {
  getOrderActionConfirmCopy,
  type OrderActionConfirmCopy,
} from "@/modules/orders/lib/order-action-confirm";
import {
  useConfirmDraftMutation,
  useDiscardDraftMutation,
} from "@/modules/orders/services/useDraftActionMutations";
import { useToast } from "@a2z/ui";

/* ── Types ──────────────────────────────────────────────────────────────── */

export type SecondaryAction = {
  key: string;
  labelKey: string;
  run: () => void;
  danger?: boolean;
};

export type DeclinePanelState = {
  open: boolean;
  setOpen: (v: boolean) => void;
  code: DeclineReasonCode | "";
  setCode: (v: DeclineReasonCode | "") => void;
  note: string;
  setNote: (v: string) => void;
  submit: () => Promise<void>;
  canSubmit: boolean;
};

export type PrimaryAction = {
  labelKey: string;
  run: () => Promise<void>;
  disabled: boolean;
};

/** Confirmation dialog state for the forward primary action (non-review). */
export type PrimaryConfirmState = {
  open: boolean;
  setOpen: (v: boolean) => void;
  fromStatus: OrderStatus;
  toStatus: OrderStatus;
  copy: OrderActionConfirmCopy;
  /** Whether the confirmation collects an optional ETA (mark-shipped). */
  needsEta: boolean;
  /** Runs the underlying action with the given ETA, then closes the dialog. */
  run: (etaDays: number | null) => Promise<void>;
};

/** Dialog visibility/control state — callers render the dialogs themselves. */
export type OrderDialogState = {
  cancelOpen: boolean;
  setCancelOpen: (v: boolean) => void;
  discardOpen: boolean;
  setDiscardOpen: (v: boolean) => void;
  onConfirmCancel: (reasonCode: CancelReasonCode, note?: string) => Promise<void>;
  onConfirmDiscard: () => Promise<void>;
};

export type UseOrderActionsResult = {
  /** The forward / review action — null when no action is applicable. */
  primary: PrimaryAction | null;
  /**
   * Confirmation gate for the primary action — null for the review trigger
   * (no mutation) or when there is no primary. When present, the primary's
   * `run` opens this dialog instead of mutating directly.
   */
  primaryConfirm: PrimaryConfirmState | null;
  /** True when the primary is the "Review payment" trigger (no status change). */
  isReview: boolean;
  /** Whether the primary transition asks for an ETA days value. */
  needsEta: boolean;
  etaInput: string;
  setEtaInput: (v: string) => void;
  /** Secondary actions for the ⋮ overflow menu. */
  secondary: SecondaryAction[];
  /** All state for the inline decline reason panel. */
  declinePanel: DeclinePanelState;
  /**
   * Submit a decline transition directly with an explicit reason code —
   * avoids the state-update-before-submit timing issue when calling from
   * outside the inline panel (e.g. mobile DeclineOrderSheet).
   */
  submitDeclineWith: (code: DeclineReasonCode, note?: string) => Promise<void>;
  /** Dialog control state — caller renders ConfirmDialogs using this. */
  dialogState: OrderDialogState;
  isSaving: boolean;
  error: string | null;
  /** True while a draft-specific mutation is in flight. */
  isDraftSaving: boolean;
  /** Whether the current order is a draft. */
  isDraft: boolean;
};

export type UseOrderActionsArgs = {
  order: SellerOrderDetail;
  storeSlug: string;
  onReviewPayment?: () => void;
  onClose: () => void;
};

/* ── Hook ───────────────────────────────────────────────────────────────── */

export function useOrderActions({
  order,
  storeSlug,
  onReviewPayment,
  onClose,
}: UseOrderActionsArgs): UseOrderActionsResult {
  const t = useTranslations();
  const toast = useToast();

  /* ── Local state ──────────────────────────────────────────────────────── */
  const [etaInput, setEtaInput] = useState("");
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineCode, setDeclineCode] = useState<DeclineReasonCode | "">("");
  const [declineNote, setDeclineNote] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [confirmPrimaryOpen, setConfirmPrimaryOpen] = useState(false);

  /* ── Mutations ────────────────────────────────────────────────────────── */
  const { mutateAsync: confirmDraft } = useConfirmDraftMutation();
  const { mutateAsync: discardDraft } = useDiscardDraftMutation();

  const transition = useOrderTransition({
    storeSlug,
    orderId: order.id,
    onSuccess: () => {
      toast.success(t(keys.orders.statusSavedMicro));
      setEtaInput("");
      setDeclineOpen(false);
      setDeclineCode("");
      setDeclineNote("");
      setCancelOpen(false);
    },
  });

  /* ── Status machine ──────────────────────────────────────────────────── */
  const transitions = getAllowedTransitions(order.status);
  const primaryTransition =
    transitions.find((a) => a.variant === "primary") ?? null;
  const declineTransition =
    transitions.find((a) => a.variant === "danger") ?? null;
  const canReview = Boolean(onReviewPayment);
  const canCancel = getAllValidTargets(order.status).includes("cancelled");
  const isDraft = order.status === "draft";
  const isCodDraft = isDraft && order.paymentMethod === "COD";

  /* ── Primary action ─────────────────────────────────────────────────── */
  let primary: PrimaryAction | null = null;
  let isReview = false;
  // Forward target that the primary mutates to — drives the confirm copy.
  // Null for the review trigger (no mutation, no confirmation).
  let confirmTarget: OrderStatus | null = null;
  // The underlying mutation, run from the confirmation popup with its ETA.
  let rawRun: ((etaDays: number | null) => Promise<void>) | null = null;
  let primaryNeedsEta = false;

  if (isDraft) {
    if (isCodDraft) {
      const handleConfirmDraft = async (): Promise<void> => {
        setDraftSaving(true);
        try {
          await confirmDraft({ storeSlug, orderId: order.id });
          toast.success(t(keys.seller.orderStatusConfirmed));
        } catch {
          // Error is surfaced globally via the React Query MutationCache handler.
        } finally {
          setDraftSaving(false);
        }
      };
      primary = {
        labelKey: keys.seller.orderActionConfirm,
        run: async () => {},
        disabled: draftSaving,
      };
      confirmTarget = "confirmed";
      rawRun = () => handleConfirmDraft();
    }
  } else if (canReview) {
    isReview = true;
    primary = {
      // Use the same next-action label as the mobile rows (e.g. "Review payment").
      labelKey: getStatusMeta(order.status).nextLabelKey,
      run: async () => {
        onReviewPayment?.();
      },
      disabled: false,
    };
  } else if (primaryTransition) {
    const target = primaryTransition.targetStatus;
    const collectsEta = primaryTransition.needsEta;
    primary = {
      // Mobile-consistent action label ("Mark shipped", "Mark delivered", …)
      // instead of the terser machine label ("Ship").
      labelKey: getStatusMeta(order.status).nextLabelKey,
      run: async () => {},
      disabled: transition.isSaving,
    };
    confirmTarget = target;
    primaryNeedsEta = collectsEta;
    rawRun = (etaDays) =>
      transition.submit({
        status: target,
        // ETA is optional — only forward a valid non-negative value.
        statusEtaDays:
          collectsEta && etaDays !== null && Number.isFinite(etaDays) && etaDays >= 0
            ? etaDays
            : null,
      });
  }

  /* ── Confirmation gate for the forward primary action ────────────────── */
  // The review trigger (confirmTarget null) opens the proof and never mutates,
  // so it is not gated. Every real forward mutation asks for confirmation in
  // the OrderActionConfirm popup; the primary button just opens it.
  let primaryConfirm: PrimaryConfirmState | null = null;
  if (primary && confirmTarget && rawRun) {
    const run = rawRun;
    primaryConfirm = {
      open: confirmPrimaryOpen,
      setOpen: setConfirmPrimaryOpen,
      fromStatus: order.status,
      toStatus: confirmTarget,
      copy: getOrderActionConfirmCopy(confirmTarget),
      needsEta: primaryNeedsEta,
      run: async (etaDays) => {
        try {
          await run(etaDays);
        } finally {
          setConfirmPrimaryOpen(false);
        }
      },
    };
    primary = {
      ...primary,
      run: async () => {
        setConfirmPrimaryOpen(true);
      },
    };
  }

  /* ── Secondary actions ───────────────────────────────────────────────── */
  const secondary: SecondaryAction[] = [];

  if (isDraft) {
    secondary.push({
      key: "discard",
      labelKey: keys.orders.discardDraftAction,
      run: () => setDiscardOpen(true),
      danger: true,
    });
  } else {
    if (declineTransition) {
      secondary.push({
        key: "decline",
        labelKey: keys.seller.orderActionDecline,
        run: () => setDeclineOpen((open) => !open),
        danger: true,
      });
    }
    if (canCancel) {
      secondary.push({
        key: "cancel",
        labelKey: keys.common.cancel,
        run: () => setCancelOpen(true),
        danger: true,
      });
    }
  }

  /* ── Decline panel ──────────────────────────────────────────────────── */
  const submitDecline = async (): Promise<void> => {
    if (!declineCode) return;
    await transition.submit({
      status: "declined",
      statusEtaDays: null,
      declineReasonCode: declineCode,
      declineReasonNote: declineNote.trim() || undefined,
    });
  };

  /** Submit a decline with an explicit code — avoids stale state from setCode. */
  const submitDeclineWith = async (
    code: DeclineReasonCode,
    note?: string,
  ): Promise<void> => {
    await transition.submit({
      status: "declined",
      statusEtaDays: null,
      declineReasonCode: code,
      declineReasonNote: note?.trim() || undefined,
    });
  };

  const declinePanel: DeclinePanelState = {
    open: declineOpen && !canReview,
    setOpen: setDeclineOpen,
    code: declineCode,
    setCode: setDeclineCode,
    note: declineNote,
    setNote: setDeclineNote,
    submit: submitDecline,
    canSubmit: Boolean(declineCode) && !transition.isSaving,
  };

  /* ── Dialog handlers ─────────────────────────────────────────────────── */
  const onConfirmCancel = async (
    reasonCode: CancelReasonCode,
    note?: string,
  ): Promise<void> => {
    await transition.submit({
      status: "cancelled",
      statusEtaDays: null,
      cancelReasonCode: reasonCode,
      cancelReasonNote: note || undefined,
    });
  };

  const onConfirmDiscard = async (): Promise<void> => {
    setDraftSaving(true);
    try {
      await discardDraft({ storeSlug, orderId: order.id });
      toast.success(t(keys.orders.draftDiscardedNotice));
      onClose();
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    } finally {
      setDraftSaving(false);
      setDiscardOpen(false);
    }
  };

  const dialogState: OrderDialogState = {
    cancelOpen,
    setCancelOpen,
    discardOpen,
    setDiscardOpen,
    onConfirmCancel,
    onConfirmDiscard,
  };

  /* ── Return ─────────────────────────────────────────────────────────── */
  return {
    primary,
    primaryConfirm,
    isReview,
    needsEta: !isReview && Boolean(primaryTransition?.needsEta),
    etaInput,
    setEtaInput,
    secondary,
    declinePanel,
    submitDeclineWith,
    dialogState,
    isSaving: transition.isSaving || draftSaving,
    error: transition.error,
    isDraftSaving: draftSaving,
    isDraft,
  };
}
